import type { Rule } from './types';

export const triggerRules: Rule[] = [
  {
    id: 'triggers.multiple-cues',
    category: 'triggers',
    severity: 'warn',
    weight: 5,
    title: 'Has multiple distinct triggers',
    appliesTo: 'both',
    run(ctx) {
      const n = ctx.triggers.distinctTriggerCount;
      if (n >= 5) return { status: 'pass', message: `${n} distinct trigger signals (excellent).` };
      if (n >= 3) return { status: 'pass', message: `${n} distinct trigger signals.` };
      if (n >= 1) {
        return {
          status: 'warn',
          message: `Only ${n} distinct trigger signal(s) found.`,
          fix: 'Add 2 or more quoted user phrasings, file types, or "Use when" cues so Claude has multiple matching surfaces.',
        };
      }
      return {
        status: 'fail',
        message: 'No trigger signals detected (quoted phrases, "use when…", file types, or named tools).',
        fix: 'Add at least three trigger surfaces to the description so Claude can match diverse user phrasings.',
      };
    },
  },
  {
    id: 'triggers.density',
    category: 'triggers',
    severity: 'info',
    weight: 3,
    title: 'Trigger density is healthy',
    appliesTo: 'both',
    run(ctx) {
      const d = ctx.triggers.triggerDensity;
      if (d >= 1.5) return { status: 'pass', message: `Density ${d.toFixed(2)} triggers / 100 chars (excellent).` };
      if (d >= 0.8) return { status: 'pass', message: `Density ${d.toFixed(2)} triggers / 100 chars.` };
      if (d > 0) {
        return {
          status: 'warn',
          message: `Density only ${d.toFixed(2)} triggers / 100 chars.`,
          fix: 'Distribute trigger language across the description, not just in one phrase.',
        };
      }
      return { status: 'warn', message: 'No trigger language.' };
    },
  },
  {
    id: 'triggers.mentions-file-types',
    category: 'triggers',
    severity: 'info',
    weight: 2,
    title: 'Mentions relevant file types',
    appliesTo: 'both',
    run(ctx) {
      if (ctx.triggers.fileTypes.length > 0) {
        return { status: 'pass', message: `Mentions: ${ctx.triggers.fileTypes.join(', ')}.` };
      }
      // Only warn if the SKILL.md body clearly handles concrete file I/O.
      const bodyHasFileType = /\.(pdf|docx?|pptx?|xlsx?|csv|tsv|svg|png|jpe?g|fig|psd|sketch|zip)\b/i.test(ctx.parsed.body);
      const bodyTalksUpload = /\b(?:upload|export|save as|write to|read from|parse|render)\b/i.test(ctx.parsed.body);
      if (!bodyHasFileType && !bodyTalksUpload) return { status: 'pass', message: 'No file types relevant.' };
      return {
        status: 'warn',
        message: 'Skill handles files but description omits file types.',
        fix: 'Name the file extensions you handle (e.g. .pdf, .docx). The guide explicitly recommends this.',
      };
    },
  },
  {
    id: 'triggers.named-tools',
    category: 'triggers',
    severity: 'info',
    weight: 2,
    title: 'Names tools or services when relevant',
    appliesTo: 'both',
    run(ctx) {
      if (ctx.triggers.toolMentions.length > 0) {
        return { status: 'pass', message: `Names: ${ctx.triggers.toolMentions.slice(0, 5).join(', ')}.` };
      }
      return { status: 'pass', message: 'No tool/service names (may not be relevant).' };
    },
  },
  {
    id: 'triggers.has-negative',
    category: 'triggers',
    severity: 'info',
    weight: 2,
    title: 'Includes negative triggers when appropriate',
    appliesTo: 'both',
    run(ctx) {
      if (ctx.triggers.hasNegativeTrigger) {
        return { status: 'pass', message: 'Has explicit negative trigger ("do NOT use for…").' };
      }
      // For very broad-sounding skills, recommend a negative trigger.
      if (ctx.triggers.domainTerms.length < 4 && ctx.descText.wordCount >= 25) {
        return {
          status: 'warn',
          message: 'Broad-sounding skill with no negative trigger.',
          fix: 'Add a clause like "Do NOT use for X" to prevent over-triggering.',
        };
      }
      return { status: 'pass', message: 'Negative trigger not required.' };
    },
  },
  {
    id: 'triggers.not-overly-generic',
    category: 'triggers',
    severity: 'fail',
    weight: 4,
    title: 'Avoids universal-scope claims',
    appliesTo: 'both',
    run(ctx) {
      if (ctx.triggers.isOverlyGeneric) {
        return {
          status: 'fail',
          message: 'Description claims universal scope ("anything you want", "any task", "general purpose").',
          fix: 'Skills should be focused. Limit scope to a specific domain or workflow.',
        };
      }
      return { status: 'pass', message: 'Scope is appropriately bounded.' };
    },
  },
  {
    id: 'triggers.domain-specificity',
    category: 'triggers',
    severity: 'info',
    weight: 3,
    title: 'Uses domain-specific terminology',
    appliesTo: 'both',
    run(ctx) {
      const n = ctx.triggers.domainTerms.length;
      const wc = ctx.descText.wordCount;
      if (wc === 0) return { status: 'skipped', message: 'No description.' };
      const ratio = n / wc;
      if (ratio >= 0.4) return { status: 'pass', message: `${n} domain terms (specific).` };
      if (ratio >= 0.2) return { status: 'pass', message: `${n} domain terms.` };
      return {
        status: 'warn',
        message: `Only ${n} distinctive terms — description reads generic.`,
        fix: 'Name the concrete nouns, verbs, and proper-noun tools this skill operates on.',
      };
    },
  },
  {
    id: 'triggers.file-type-specificity',
    category: 'triggers',
    severity: 'info',
    weight: 2,
    title: 'File-type mentions are specific',
    appliesTo: 'both',
    guideRef: 'Planning and design → Mention file types if relevant',
    run(ctx) {
      const desc = ((ctx.parsed.frontmatter.description as string | undefined) ?? '').toLowerCase();
      // Vague filing-cabinet language without a concrete extension.
      const vagueFileNouns = /\b(?:documents?|files?|spreadsheets?|presentations?|images?)\b/.test(desc);
      if (!vagueFileNouns) {
        return { status: 'pass', message: 'No vague file-type language in description.' };
      }
      // Specific extensions or named formats anywhere in the description.
      const specific = /\.(?:pdf|docx?|xlsx?|csv|tsv|json|yaml|yml|md|html?|xml|png|jpe?g|gif|svg|webp|mp3|wav|mp4|mov|webm|zip|tar|gz)\b|\b(?:pdf|docx|xlsx|csv|json|yaml|markdown|html|svg|png|jpeg|mp3|mp4)\b/i.test(desc);
      if (specific) {
        return { status: 'pass', message: 'Description names specific file types.' };
      }
      return {
        status: 'warn',
        message: 'Description mentions files generically ("documents", "spreadsheets") without naming the formats.',
        fix: 'Replace generic file nouns with extensions or named formats — e.g. "PDF invoices", ".docx contracts", ".xlsx pricing sheets".',
      };
    },
  },
];
